import {
  JobsPayload,
  RootState,
  selectActiveSession,
  selectFocusedAccounts,
  selectFocusedMyAccount,
  selectFocusedJobs,
  SessionWithState,
} from '@helios/state-management'
import { compareSets } from '@helios/utils'
import { Store } from '@ngrx/store'
import { combineLatest, Observable, of } from 'rxjs'
import { distinctUntilChanged, switchMap } from 'rxjs/operators'

/**
 * true = content can be shown
 * false = content should not exist on the dom
 */
export type ContentPredicateStream = Observable<boolean>

/**
 * To verify if account is loaded or not
 */
export function getIsAccountLoaded(
  store: Store<RootState>,
): ContentPredicateStream {
  return store.select(selectActiveSession).pipe(
    distinctUntilChanged((a, b) => a?.id === b?.id),
    switchMap(session => {
      /**
       * emitting false before true to ensure component destruction. Below distinctUntilChanged will get rid of the
       * false if the prior value was false.
       */
      return sessionHasAccounts(session) ? of(true) : of(false)
    }),
    distinctUntilChanged(),
  )
}

/**
 * To verify if account is selected in account list panel
 */
export function getIsAccountSelected(
  store: Store<RootState>,
): ContentPredicateStream {
  return store.select(selectFocusedAccounts).pipe(
    distinctUntilChanged((a, b) =>
      compareSets(new Set<number>(a), new Set<number>(b)),
    ),
    switchMap(accounts => {
      /**
       * emitting false before true to ensure component destruction. Below distinctUntilChanged will get rid of the
       * false if the prior value was false.
       */
      return arrayIsNotEmpty(accounts) ? of(true) : of(false)
    }),
    distinctUntilChanged(),
  )
}

/**
 * To verify if aggregate is focused in my accounts panel
 */
export function getIsAggregateFocused(
  store: Store<RootState>,
): ContentPredicateStream {
  return store.select(selectFocusedMyAccount).pipe(
    distinctUntilChanged((a, b) => a?.accountId === b?.accountId),
    switchMap(aggregate => {
      /**
       * emitting false before true to ensure component destruction. Below distinctUntilChanged will get rid of the
       * false if the prior value was false.
       */
      return aggregate ? of(true) : of(false)
    }),
    distinctUntilChanged(),
  )
}

/**
 * To verify if job needs to be highlighted in jobs panel
 */
export function getIsJobFocused(
  store: Store<RootState>,
): ContentPredicateStream {
  return store.select(selectFocusedJobs).pipe(
    distinctUntilChanged(),
    switchMap(job => (hasSelectedJob(job) ? of(true) : of(false))),
    distinctUntilChanged(),
  )
}

/**
 * To verify whether account selected or loaded or job focused
 */
export function getIsAccountSelectedOrLoadedOrJobFocused(
  store: Store<RootState>,
): ContentPredicateStream {
  return mergeContentPredicateStream(
    getIsAccountSelected(store),
    getIsAccountLoaded(store),
    getIsJobFocused(store),
  )
}

/**
 * Visible for testing.
 * @internal
 * @param stream$[]
 */
export function mergeContentPredicateStream(
  ...streams$: ContentPredicateStream[]
) {
  return combineLatest(streams$).pipe(
    switchMap(streams => (streams.some(it => !!it) ? of(true) : of(false))),
    distinctUntilChanged(),
  )
}

/**
 * To verify if session has account information
 */
function sessionHasAccounts(session: SessionWithState | undefined): boolean {
  return session != null && session.loadedAccountIds.length > 0
}

/**
 * To verify if given array is not empty
 */
function arrayIsNotEmpty(a: number[]): boolean {
  return a != null && a.length > 0
}

/**
 * To verify if selected job payload has data
 */
function hasSelectedJob(job: JobsPayload | undefined): boolean {
  return job != null && (job.accountIds.length > 0 || job.jobIds.length > 0)
}
